import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { AlertTriangle, Check, Trash2, ChevronDown, ExternalLink, Clock, CheckCircle, Pill, Droplet, Wind, Stethoscope, BookOpen } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SERVICE_LABELS = { ground: "PCP/ACP-G", sfa_emr: "SFA/EMR", wes: "PCP-PS", air_adult: "Air Adult", air_pediatric: "Air Peds" };

const CATEGORY_META = {
  protocol:   { icon: BookOpen,    label: "Protocol",   color: "text-slate-400 border-slate-500/40 bg-slate-500/10" },
  medication: { icon: Pill,        label: "Medication", color: "text-green-400 border-green-500/40 bg-green-500/10" },
  infusion:   { icon: Droplet,     label: "Infusion",   color: "text-purple-400 border-purple-500/40 bg-purple-500/10" },
  ventilator: { icon: Wind,        label: "Ventilator", color: "text-cyan-400 border-cyan-500/40 bg-cyan-500/10" },
  procedure:  { icon: Stethoscope, label: "Procedure",  color: "text-amber-400 border-amber-500/40 bg-amber-500/10" },
};

const AdminErrorReports = () => {
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("open");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const { token } = useAuth();

  const fetchReports = async () => {
    try {
      const params = new URLSearchParams();
      if (filter !== "all") params.set("status", filter);
      if (categoryFilter !== "all") params.set("category", categoryFilter);
      const qs = params.toString() ? `?${params.toString()}` : "";
      const res = await axios.get(`${API}/error-reports${qs}`);
      setReports(res.data);
    } catch {
      toast.error("Failed to load reports");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { fetchReports(); }, [filter, categoryFilter]); // eslint-disable-line react-hooks/exhaustive-deps

  const handleResolve = async (id) => {
    try {
      await axios.patch(`${API}/error-reports/${id}`, { status: "resolved" });
      toast.success("Marked as resolved");
      fetchReports();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleReopen = async (id) => {
    try {
      await axios.patch(`${API}/error-reports/${id}`, { status: "open" });
      toast.success("Reopened");
      fetchReports();
    } catch {
      toast.error("Failed to update");
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this report?")) return;
    try {
      await axios.delete(`${API}/error-reports/${id}`);
      toast.success("Deleted");
      fetchReports();
    } catch {
      toast.error("Failed to delete");
    }
  };

  const openCount = reports.filter(r => r.status === "open").length;

  if (loading) return <div className="p-8 text-center text-muted-foreground">Loading reports...</div>;

  return (
    <div className="container mx-auto px-4 py-6 max-w-4xl space-y-6">
      <div className="flex items-center justify-between">
        <h1 className="font-heading text-2xl font-bold flex items-center gap-2">
          <AlertTriangle className="w-6 h-6 text-amber-500" />
          Error Reports
          {openCount > 0 && <Badge className="bg-amber-500 text-white">{openCount} open</Badge>}
        </h1>
        <Link to="/admin/dashboard"><Button variant="outline" size="sm">Dashboard</Button></Link>
      </div>

      {/* Filter */}
      <div className="flex gap-2">
        {["open", "resolved", "all"].map((f) => (
          <Button
            key={f}
            size="sm"
            variant={filter === f ? "default" : "outline"}
            onClick={() => setFilter(f)}
            data-testid={`filter-${f}`}
          >
            {f === "open" && <Clock className="w-3.5 h-3.5 mr-1" />}
            {f === "resolved" && <CheckCircle className="w-3.5 h-3.5 mr-1" />}
            {f.charAt(0).toUpperCase() + f.slice(1)}
          </Button>
        ))}
      </div>

      {/* Category filter */}
      <div className="flex gap-1.5 flex-wrap" data-testid="category-filter-row">
        <Button
          size="sm"
          variant={categoryFilter === "all" ? "default" : "outline"}
          onClick={() => setCategoryFilter("all")}
          className="h-7 text-xs"
          data-testid="category-filter-all"
        >
          All categories
        </Button>
        {Object.entries(CATEGORY_META).map(([key, meta]) => {
          const Icon = meta.icon;
          return (
            <Button
              key={key}
              size="sm"
              variant={categoryFilter === key ? "default" : "outline"}
              onClick={() => setCategoryFilter(key)}
              className="h-7 text-xs gap-1"
              data-testid={`category-filter-${key}`}
            >
              <Icon className="w-3 h-3" />
              {meta.label}
            </Button>
          );
        })}
      </div>

      {reports.length === 0 && (
        <Card>
          <CardContent className="py-12 text-center text-muted-foreground">
            {filter === "open" ? "No open error reports" : filter === "resolved" ? "No resolved reports" : "No reports yet"}
          </CardContent>
        </Card>
      )}

      {reports.map((report) => {
        const cat = report.category || "protocol";
        const meta = CATEGORY_META[cat] || CATEGORY_META.protocol;
        const CatIcon = meta.icon;
        const subjId = report.subject_id || report.protocol_id;
        const subjName = report.subject_name || report.protocol_name;
        // Only "protocol" and "procedure" have direct navigation in the app;
        // medication / infusion / ventilator reports display as plain badges
        const linkHref =
          cat === "protocol" ? `/protocol/${subjId}` :
          cat === "procedure" ? `/procedure/${subjId}` : null;

        return (
        <Card
          key={report.id}
          className={`${report.status === "open" ? "border-amber-500/30" : "border-border/50 opacity-75"}`}
          data-testid={`report-${report.id}`}
        >
          <CardContent className="py-4">
            <div className="flex items-start justify-between gap-4">
              <div className="flex-1 space-y-2">
                {/* Header */}
                <div className="flex items-center gap-2 flex-wrap">
                  <Badge variant={report.status === "open" ? "default" : "secondary"} className={report.status === "open" ? "bg-amber-500" : ""}>
                    {report.status === "open" ? <Clock className="w-3 h-3 mr-1" /> : <CheckCircle className="w-3 h-3 mr-1" />}
                    {report.status}
                  </Badge>
                  <Badge variant="outline" className={`gap-1 ${meta.color}`} data-testid={`report-category-${cat}`}>
                    <CatIcon className="w-3 h-3" />
                    {meta.label}
                  </Badge>
                  {report.service && (
                    <Badge variant="outline">{SERVICE_LABELS[report.service] || report.service}</Badge>
                  )}
                  {linkHref ? (
                    <Link to={linkHref} className="text-sm font-medium text-primary hover:underline flex items-center gap-1">
                      {subjName} <ExternalLink className="w-3 h-3" />
                    </Link>
                  ) : (
                    <span className="text-sm font-medium">{subjName}</span>
                  )}
                </div>

                {/* Details */}
                <p className="text-sm bg-muted/30 rounded p-3">{report.details}</p>

                {/* Meta */}
                <div className="flex items-center gap-4 text-xs text-muted-foreground">
                  <span>Reported by: <strong>{report.reported_by || "anonymous"}</strong></span>
                  <span>{new Date(report.created_at).toLocaleString()}</span>
                </div>
              </div>

              {/* Actions */}
              <div className="flex flex-col gap-1 shrink-0">
                {report.status === "open" ? (
                  <Button size="sm" variant="outline" className="text-green-500 border-green-500/30 hover:bg-green-500/10" onClick={() => handleResolve(report.id)}>
                    <Check className="w-3.5 h-3.5 mr-1" />Resolve
                  </Button>
                ) : (
                  <Button size="sm" variant="outline" onClick={() => handleReopen(report.id)}>
                    <Clock className="w-3.5 h-3.5 mr-1" />Reopen
                  </Button>
                )}
                <Button size="sm" variant="ghost" className="text-destructive hover:bg-destructive/10" onClick={() => handleDelete(report.id)}>
                  <Trash2 className="w-3.5 h-3.5 mr-1" />Delete
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
        );
      })}
    </div>
  );
};

export default AdminErrorReports;
